import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useIsNativeApp } from '../utils/platform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isNativeApp = useIsNativeApp();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      try {
        const saved = localStorage.getItem('user');
        setCurrentUser(saved ? JSON.parse(saved) : null);
      } catch {
        setCurrentUser(null);
      }
    };
    loadUser();
    window.addEventListener('userUpdated', loadUser);
    window.addEventListener('storage', loadUser);
    return () => {
      window.removeEventListener('userUpdated', loadUser);
      window.removeEventListener('storage', loadUser);
    };
  }, []);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'GoodJob',
    tagline: '',
    description: 'Connect with skilled professionals for any job. From home repairs to consulting.',
    contact_email: '',
    contact_phone: '',
    address: '',
    show_email: true,
    show_phone: true,
    show_address: true,
    social_facebook: null,
    social_twitter: null,
    social_instagram: null,
    social_linkedin: null,
    about_us: null,
    privacy_policy: null,
    terms_of_service: null,
    success_stories: null,
    resources: null
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/company-info`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  // Check if content pages have content
  const hasAboutUs = companyInfo.about_us && companyInfo.about_us.trim().length > 0;
  const hasPrivacyPolicy = companyInfo.privacy_policy && companyInfo.privacy_policy.trim().length > 0;
  const hasTermsOfService = companyInfo.terms_of_service && companyInfo.terms_of_service.trim().length > 0;
  const hasSuccessStories = companyInfo.success_stories && companyInfo.success_stories.trim().length > 0;
  const hasResources = companyInfo.resources && companyInfo.resources.trim().length > 0;

  const isLoggedIn = !!currentUser;
  const effectiveRole = currentUser?.active_role || currentUser?.role;
  const isClient = isLoggedIn && effectiveRole === 'client';
  const isProvider = isLoggedIn && effectiveRole === 'provider';
  const hasProviderRole = currentUser?.available_roles?.includes('provider');

  const handleBecomeProvider = async () => {
    if (!isLoggedIn) {
      navigate('/register');
      return;
    }
    if (hasProviderRole) {
      try {
        await axios.put(`${API}/user/switch-role?new_role=provider`);
        window.location.href = '/dashboard';
      } catch {
        navigate('/dashboard');
      }
    } else {
      navigate('/register');
    }
  };

  // Hidden inside the native app — footer info lives in the mobile menu drawer instead.
  if (isNativeApp) return null;

  return (
    <footer className="bg-gray-900 text-white mt-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-bold mb-4" data-testid="footer-company-name">
              {companyInfo.company_name || 'GoodJob'}
            </h3>
            <p className="text-gray-400" data-testid="footer-description">
              {companyInfo.description || t('footer.tagline')}
            </p>
            {/* Social Media Links */}
            <div className="flex space-x-4 mt-4">
              {companyInfo.social_facebook && (
                <a href={companyInfo.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" data-testid="social-facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/></svg>
                </a>
              )}
              {companyInfo.social_twitter && (
                <a href={companyInfo.social_twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" data-testid="social-twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.44 4.83c-.8.37-1.5.38-2.22.02.93-.56.98-.96 1.32-2.02-.88.52-1.86.9-2.9 1.1-.82-.88-2-1.43-3.3-1.43-2.5 0-4.55 2.04-4.55 4.54 0 .36.03.7.1 1.04-3.77-.2-7.12-2-9.36-4.75-.4.67-.6 1.45-.6 2.3 0 1.56.8 2.95 2 3.77-.74-.03-1.44-.23-2.05-.57v.06c0 2.2 1.56 4.03 3.64 4.44-.67.2-1.37.2-2.06.08.58 1.8 2.26 3.12 4.25 3.16C5.78 18.1 3.37 18.74 1 18.46c2 1.3 4.4 2.04 6.97 2.04 8.35 0 12.92-6.92 12.92-12.93 0-.2 0-.4-.02-.6.9-.63 1.96-1.22 2.56-2.14z"/></svg>
                </a>
              )}
              {companyInfo.social_instagram && (
                <a href={companyInfo.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" data-testid="social-instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
              )}
              {companyInfo.social_linkedin && (
                <a href={companyInfo.social_linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" data-testid="social-linkedin">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              )}
            </div>
          </div>
          
          {/* For Clients */}
          {!isProvider && (
            <div>
              <h4 className="font-semibold mb-4">{t('footer.forClients')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/services" className="hover:text-white" data-testid="footer-browse-services">{t('footer.browseServices')}</Link></li>
                {isClient ? (
                  <li><Link to="/create-job" className="hover:text-white" data-testid="footer-post-job">{t('footer.postJob')}</Link></li>
                ) : !isLoggedIn ? (
                  <li><Link to="/register" className="hover:text-white" data-testid="footer-post-job">{t('footer.postJob')}</Link></li>
                ) : null}
                <li><Link to="/help" className="hover:text-white" data-testid="footer-how-it-works">{t('footer.howItWorks')}</Link></li>
              </ul>
            </div>
          )}
          
          {/* For Providers */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.forProviders')}</h4>
            <ul className="space-y-2 text-gray-400">
              {isProvider ? (
                <li><Link to="/jobs" className="hover:text-white" data-testid="footer-browse-jobs">{t('footer.availableJobs')}</Link></li>
              ) : (
                <li>
                  <button onClick={handleBecomeProvider} className="hover:text-white text-left" data-testid="footer-become-provider">
                    {t('footer.becomeProvider')}
                  </button>
                </li>
              )}
              {hasSuccessStories && (
                <li><Link to="/success-stories" className="hover:text-white" data-testid="footer-success-stories">{t('footer.successStories')}</Link></li>
              )}
              {hasResources && (
                <li><Link to="/resources" className="hover:text-white" data-testid="footer-resources">{t('footer.providerAgreement')}</Link></li>
              )}
            </ul>
          </div>
          
          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2 text-gray-400">
              {hasAboutUs && (
                <li><Link to="/about" className="hover:text-white" data-testid="footer-about-us">{t('footer.aboutUs')}</Link></li>
              )}
              <li><Link to="/help" className="hover:text-white" data-testid="footer-contact">{t('footer.contact')}</Link></li>
              {hasPrivacyPolicy && (
                <li><Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-policy">{t('footer.privacyPolicy')}</Link></li>
              )}
              {hasTermsOfService && (
                <li><Link to="/terms-of-service" className="hover:text-white" data-testid="footer-terms-of-service">{t('footer.termsOfService')}</Link></li>
              )}
            </ul>
          </div>
        </div>
        
        {/* Contact Info */}
        {(() => {
          const items = [];
          if (companyInfo.contact_email && companyInfo.show_email !== false) {
            items.push(
              <div key="email" className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                <a href={`mailto:${companyInfo.contact_email}`} className="hover:text-white" data-testid="footer-email">{companyInfo.contact_email}</a>
              </div>
            );
          }
          if (companyInfo.contact_phone && companyInfo.show_phone !== false) {
            items.push(
              <div key="phone" className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                <span data-testid="footer-phone">{companyInfo.contact_phone}</span>
              </div>
            );
          }
          if (companyInfo.address && companyInfo.show_address !== false) {
            items.push(
              <div key="address" className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <span data-testid="footer-address">{companyInfo.address}</span>
              </div>
            );
          }
          if (items.length === 0) return null;
          return (
            <div className="border-t border-gray-800 mt-8 pt-8">
              <div className={`flex flex-col md:flex-row text-gray-400 text-sm ${items.length === 1 ? 'justify-center' : items.length === 2 ? 'justify-around' : 'justify-between'} gap-4`}>
                {items}
              </div>
            </div>
          );
        })()}
        
        {/* Copyright */}
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p data-testid="footer-copyright">&copy; {new Date().getFullYear()} {companyInfo.company_name || 'GoodJob'}. {t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;